import { createContext, useContext, useMemo, type ReactNode } from "react";

/**
 * Wording for generic controls (searchable selects, date pickers, side-scroll arrows, upload checks). The defaults are
 * English; each app wraps its tree in `UiStringsProvider` with translated strings, so pages don't pass labels one by one.
 */
export interface UiStrings {
  scrollLeft: string;
  scrollRight: string;
  search: string;
  noResults: string;
  clear: string;
  loading: string;
  selectPlaceholder: string;
  pickDate: string;
  pickDateTime: string;
  previousMonth: string;
  nextMonth: string;
  today: string;
  time: string;
  month: string;
  year: string;
  hours: string;
  minutes: string;
  done: string;
  /** BCP 47 language for month and weekday names (e.g. "hi"). */
  locale: string;
  /** "photo.png is larger than 5 MB." */
  fileTooLarge: (name: string, max: string) => string;
  /** "notes.exe isn't an allowed file type (JPG, PNG)." */
  fileType: (name: string, types: string) => string;
  chooseFiles: string;
  /** "Up to 5 files · JPG, PNG · 10 MB each" */
  fileLimits: (count: number, types: string, size: string) => string;
  /** "Up to 5 files · JPG, PNG, PDF · 20 MB in total" (when all files share one size limit) */
  fileLimitsTotal: (count: number, types: string, size: string) => string;
  filesTooLarge: (size: string) => string;
  removeFile: (name: string) => string;
}

export const defaultUiStrings: UiStrings = {
  scrollLeft: "Scroll left",
  scrollRight: "Scroll right",
  search: "Search…",
  noResults: "No matches",
  clear: "Clear",
  loading: "Loading…",
  selectPlaceholder: "Select…",
  pickDate: "Pick a date",
  pickDateTime: "Pick date and time",
  previousMonth: "Previous month",
  nextMonth: "Next month",
  today: "Today",
  time: "Time",
  month: "Month",
  year: "Year",
  hours: "Hours",
  minutes: "Minutes",
  done: "Done",
  locale: "en",
  fileTooLarge: (name, max) => `${name} is larger than ${max}.`,
  fileType: (name, types) => `${name} isn't an allowed file type (${types}).`,
  chooseFiles: "Choose files",
  fileLimits: (count, types, size) => `Up to ${count} files · ${types} · ${size} each`,
  fileLimitsTotal: (count, types, size) => `Up to ${count} files · ${types} · ${size} in total`,
  filesTooLarge: (size) => `Some files weren't added: all files together can be at most ${size}.`,
  removeFile: (name) => `Remove ${name}`,
};

const UiStringsContext = createContext<UiStrings>(defaultUiStrings);

export function UiStringsProvider({
  strings,
  children,
}: {
  strings: Partial<UiStrings>;
  children: ReactNode;
}) {
  const value = useMemo(() => ({ ...defaultUiStrings, ...strings }), [strings]);
  return <UiStringsContext.Provider value={value}>{children}</UiStringsContext.Provider>;
}

export function useUiStrings(): UiStrings {
  return useContext(UiStringsContext);
}
